import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Project } from '../../../api/models/project';
import { User } from '../../../api/models/user';
import { AdminSandbox } from '../../admin.sandbox';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Camera } from '../../../api/models/camera';
import { NewUserData, UserFormDialogComponent } from './user-form-dialog.component';

// Import Chart.js (make sure to install it via npm install chart.js)
import { Chart, ChartConfiguration } from 'chart.js';
  
interface MediaStats {
  camera: string;
  day: string; // or Date if you parse it
  pictureCount: number;
}

@Component({
  selector: 'app-project-form-page',
  templateUrl: './project-form-page.component.html',
  styleUrls: ['./project-form-page.component.css']
})
export class ProjectFormPageComponent implements OnInit, OnDestroy {

  @ViewChild('statsChart', { static: false }) statsChart!: ElementRef;

  alive = true;
  
  // Observables
  project$: Observable<Project>;
  allUsers$: Observable<User[]>;
  projectUsers$: Observable<User[]>;
  cameras$: Observable<Camera[]>;

  // Local properties for binding
  project: Project;
  totalCameras: number = 0;
  totalUsers: number = 0;
  totalPictures: number = 0;
  firstDate: string;
  lastDate: string;
  projectDurationMonths: number = 0;
  loadingStats: boolean = true; // Added loading flag

  // Columns for Users table (without the id column)
  userDisplayedColumns: string[] = ['email', 'actions'];
  // Columns for Cameras table (with the id column)
  cameraDisplayedColumns: string[] = ['id', 'email', 'actions'];

  // Chart instance
  chart: Chart;

  constructor(
    private sandbox: AdminSandbox, 
    private location: Location, 
    public dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    // Load the project observable
    this.project$ = this.sandbox.getSelectedProject();
    this.allUsers$ = this.sandbox.getUsers();
    
    this.project$
      .pipe(take(1))
      .subscribe((proj) => {
        if (proj) {
          this.project = proj;
          // Load related data
          this.projectUsers$ = this.sandbox.getSelectedProjectUser(proj.id);
          this.cameras$ = this.sandbox.getCameraProjects(proj.id);

          // Calculate total cameras
          this.cameras$.pipe(take(1)).subscribe((cams) => {
            this.totalCameras = cams.length;
          });
          // Calculate total users
          this.projectUsers$.pipe(take(1)).subscribe((users) => {
            this.totalUsers = users.length;
          });

          // Load the project stats
          this.sandbox.loadProjectStatsbyProject(proj.id)
            .then((stats: MediaStats[]) => {
              // 1. Total pictures
              this.totalPictures = stats.reduce((acc, item) => acc + item.pictureCount, 0);

              // 2. Determine union of months (formatted YYYY-MM)
              const monthsSet = new Set<string>();
              stats.forEach(stat => {
                const date = new Date(stat.day);
                const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                monthsSet.add(monthKey);
              });
              const chartLabels = Array.from(monthsSet).sort((a, b) => 
                new Date(a + '-01').getTime() - new Date(b + '-01').getTime()
              );

              // Compute project duration in months, if any month exists.
              if (chartLabels.length > 0) {
                const firstMonthDate = new Date(chartLabels[0] + '-01');
                const lastMonthDate = new Date(chartLabels[chartLabels.length - 1] + '-01');
                const durationInMonths = (lastMonthDate.getFullYear() - firstMonthDate.getFullYear()) * 12 +
                                         (lastMonthDate.getMonth() - firstMonthDate.getMonth()) + 1;
                this.projectDurationMonths = durationInMonths;
              } else {
                this.projectDurationMonths = 0;
              }

              // 3. Group stats by camera and accumulate picture count per month
              const dataByCamera: { [camera: string]: { [month: string]: number } } = {};
              stats.forEach(stat => {
                const camera = stat.camera;
                const date = new Date(stat.day);
                const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                if (!dataByCamera[camera]) {
                  dataByCamera[camera] = {};
                }
                dataByCamera[camera][monthKey] = (dataByCamera[camera][monthKey] || 0) + stat.pictureCount;
              });

              // 4. Create datasets for each camera.
              const colors = [
                'rgba(75,192,192,1)', 
                'rgba(192,75,192,1)', 
                'rgba(75,75,192,1)', 
                'rgba(192,192,75,1)'
              ];
              const datasets = Object.keys(dataByCamera).map((camera, index) => {
                const cameraData = dataByCamera[camera];
                const data = chartLabels.map(month => cameraData[month] || 0);
                const borderColor = colors[index % colors.length];
                const backgroundColor = borderColor.replace(/[\d\.]+\)$/, '0.4)');
                return {
                  label: camera,
                  data: data,
                  borderColor: borderColor,
                  backgroundColor: backgroundColor,
                  borderWidth: 2,
                  fill: false,
                  tension: 0.1
                };
              });

              // 5. Configure and render the chart
              const config: ChartConfiguration<'line', number[], string> = {
                type: 'line',
                data: {
                  labels: chartLabels,
                  datasets: datasets
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: 'Month (YYYY-MM)'
                      }
                    },
                    y: {
                      title: {
                        display: true,
                        text: 'Picture Count'
                      },
                      beginAtZero: true
                    }
                  }
                }
              };

              // Mark stats as loaded.
              this.loadingStats = false;

              // Delay chart creation until the next tick (view update)
              setTimeout(() => {
                this.chart = new Chart(this.statsChart.nativeElement, config);
              }, 0);
            })
            .catch(error => {
              console.error('Error loading project stats:', error);
              this.loadingStats = false;
            });
        }
      });
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  updateCameraOrientation(camera: Camera): void {
    this.sandbox.updateCameraOrientation(camera).subscribe(
      () => {
        this.snackBar.open('Camera orientation updated successfully', 'OK', { duration: 3000 });
      },
      error => {
        console.error('Error updating camera orientation', error);
        this.snackBar.open('Error updating camera orientation', 'Dismiss', { duration: 3000 });
      }
    );
  }

  get projectStatus(): string {
    return this.project && this.project.active ? 'Active' : 'Inactive';
  }

  onSave(): void {
    if (this.project.id) {
      this.sandbox.updateProject(this.project).subscribe(
        () => {
          this.snackBar.open('Project saved successfully', 'OK', { duration: 3000 });
        },
        error => {
          console.error(error);
          this.snackBar.open('Error saving project', 'Dismiss', { duration: 3000 });
        }
      );
    } else {
      // Create new project logic here.
    }
  }

  onCancel(): void {
    this.location.back();
  }

  openAddUserDialog(): void {
    this.allUsers$.pipe(take(1)).subscribe(users => {
      console.log('All users:', users);
      const dialogRef = this.dialog.open(UserFormDialogComponent, {
        width: '400px',
        data: { existingUsers: users }
      });
  
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (result.mode === 'new' && result.newUser) {
            this.addNewUser({ user: result.newUser, password: result.newUser.password });
          } else if (result.mode === 'existing' && result.existingUser) {
            this.addUser(result.existingUser);
          }
        }
      });
    });
  }

  addNewUser(data: any) {
    this.allUsers$.pipe(take(1)).subscribe(users => {
      if (!users.find(u => u.email === data.user.email)) {
        this.sandbox.createUserToProject(data.user, data.password, this.project.id)
          .subscribe(() => {
            this.snackBar.open('User created successfully', 'OK', { duration: 3000 });
            this.refreshProjectUsers();
          });
      }
    });
  }

  addUser(user: User) {
    const userproject = { projectId: this.project.id, userId: user.id };
    this.sandbox.getSelectedProjectUser(this.project.id).pipe(take(1))
      .subscribe(users => {
        if (!users.find(u => u.email === user.email)) {
          this.sandbox.createUserProject(userproject)
            .subscribe(() => {
              this.snackBar.open('User added successfully', 'OK', { duration: 3000 });
              this.refreshProjectUsers();
            });
        }
      });
  }

  removeUser(userId: number): void {
    this.project$.pipe(take(1)).subscribe(proj => {
      if (proj) {
        const userproject = { projectId: proj.id, userId: userId };
        this.sandbox.deleteUserProject(userproject)
          .subscribe(() => {
            this.snackBar.open('User removed successfully', 'OK', { duration: 3000 });
            this.refreshProjectUsers();
          });
      }
    });
  }

  private refreshProjectUsers() {
    this.projectUsers$ = this.sandbox.getSelectedProjectUser(this.project.id);
  }
}
